/**
 * Redux state + dispatch wiring for CheckoutNew
 */
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import type { AnyAction, Dispatch } from "redux";

import {
  createCardConnectPopup,
  createErrorAlertPopup,
  createSpinnerPopup,
} from "../actions/popup";
import { createCheckout, createEditCheckout } from "../actions/shop";
import { getAddress, getContact, getFullCart } from "../selectors";
import { getAddressDropdown, getContactDropdown } from "../selectors/dropdowns";

import type {
  CheckoutContact,
  CheckoutOwnProps,
  CheckoutState,
  CheckoutStateProps,
  CheckoutDispatchProps,
  OrderMetadataRecord,
  AppliedDiscount,
  AddressEntity,
  ContactEntity,
  CheckoutCart,
} from "../components/shop/Checkout/types";

type ReduxRoot = object;

export default function useCheckoutStateProps(
  ownProps: CheckoutOwnProps,
): CheckoutStateProps & CheckoutDispatchProps {
  const dispatch = useDispatch<Dispatch<AnyAction>>();
  const store = useStore<ReduxRoot>();

  const {
    entityId,
    entityType = "SHOP",
    contact: ownContact,
    buy_inventory,
  } = ownProps;

  const cart = useSelector((state: ReduxRoot) =>
    getFullCart(state, { buy_inventory }),
  ) as CheckoutCart;

  const productQuestionsData = useSelector(
    (state: ReduxRoot) =>
      (
        state as {
          temp?: { productQuestions?: { data?: Record<string, unknown> } };
        }
      )?.temp?.productQuestions?.data,
  );

  const items_metadata = useMemo(() => {
    return Object.values(productQuestionsData ?? {}).flatMap((v) =>
      Object.values((v as object) ?? {}),
    );
  }, [productQuestionsData]);

  const rawContacts = useSelector((state: ReduxRoot) =>
    getContactDropdown(state, { parent_id: entityId }),
  );

  const contacts = useMemo(() => {
    let list = rawContacts.filter((c: ContactEntity) =>
      [entityType, `${entityType}-BILLING`, `${entityType}-SHIPPING`].includes(
        c.company_type ?? "",
      ),
    );

    if (ownContact && typeof ownContact === "object") {
      list = list.filter(
        (c: ContactEntity) =>
          c.contact_id === (ownContact as ContactEntity).contact_id,
      );
    }

    return list;
  }, [rawContacts, entityType, ownContact]);

  const rawAddresses = useSelector((state: ReduxRoot) =>
    getAddressDropdown(state, { parent_id: entityId }),
  );

  const addresses = useMemo(() => {
    let list = rawAddresses.filter(
      (a: AddressEntity) => a.parent_type === entityType,
    );

    if (ownContact && typeof ownContact === "object") {
      list = list.filter(
        (a: AddressEntity) =>
          a.address_id ===
          (ownContact as ContactEntity).contact_default_address_id,
      );
    }

    return list;
  }, [rawAddresses, entityType, ownContact]);

  const checkout = useSelector(
    (state: ReduxRoot) =>
      (state as { temp?: { checkout?: Partial<CheckoutState> } })?.temp
        ?.checkout ?? {},
  );

  const orderTotal = useMemo(() => {
    return cart.items.reduce(
      (t: number, i: { subtotal: number }) => t + i.subtotal,
      0,
    );
  }, [cart.items]);

  const getAddressFn = useCallback(
    (address_id: string) =>
      getAddress(store.getState() as ReduxRoot, { address_id }),
    [store],
  );

  const getContactFn = useCallback(
    (contact_id: string) =>
      getContact(store.getState() as ReduxRoot, { contact_id }),
    [store],
  );

  const onCheckout = useCallback(
    (
      billing_info: unknown,
      shipping_info: unknown,
      breakdowns: unknown[],
      artworks: Record<string, string>,
      comment: string,
      payment_data: unknown,
      shipping_range_id: string | null,
      customer: CheckoutContact | null,
      checked_out_amount: string,
      order_metadata: OrderMetadataRecord,
      applied_discount: AppliedDiscount | null,
      customer_po: string,
      redirect: boolean,
      client_id: string | null = null,
      onRedirect: ((...args: unknown[]) => void) | null = null,
      items_metadata: unknown[] | null = null,
      suppressErrorPopup: boolean = false,
    ) => {
      dispatch(createSpinnerPopup("Processing...", true) as AnyAction);
      return dispatch(
        createCheckout(
          entityId,
          billing_info,
          shipping_info,
          breakdowns,
          artworks,
          comment,
          payment_data,
          shipping_range_id,
          customer,
          checked_out_amount,
          order_metadata,
          applied_discount,
          customer_po,
          redirect,
          client_id,
          false,
          onRedirect,
          items_metadata,
          suppressErrorPopup,
        ) as unknown as AnyAction,
      ) as unknown as Promise<unknown>;
    },
    [dispatch, entityId],
  );

  const onCreateCardConnectPopup = useCallback(
    (
      shopArg: unknown,
      checkoutTotal: string,
      billing_info: unknown,
      shipping_info: unknown,
      breakdowns: unknown[],
      artworks: Record<string, string>,
      comment: string,
      shipping_range_id: string | null,
      customer: CheckoutContact | null,
      order_metadata: OrderMetadataRecord,
      applied_discount: AppliedDiscount | null,
      customer_po: string,
      items_metadata?: unknown[],
    ) => {
      dispatch(
        createCardConnectPopup(
          shopArg,
          checkoutTotal,
          billing_info,
          shipping_info,
          breakdowns,
          artworks,
          comment,
          shipping_range_id,
          customer,
          order_metadata,
          applied_discount,
          customer_po,
          items_metadata,
        ) as AnyAction,
      );
    },
    [dispatch],
  );

  const onCreateErrorPopup = useCallback(
    (error_msg: string, onClose: (() => void) | null = null) =>
      dispatch(createErrorAlertPopup(error_msg, onClose) as AnyAction),
    [dispatch],
  );

  const onEditCheckout = useCallback(
    (data: Partial<CheckoutState>) =>
      dispatch(createEditCheckout(entityId, data) as AnyAction),
    [dispatch, entityId],
  );

  return useMemo(
    () => ({
      cart,
      items_metadata,
      addresses,
      contacts,
      checkout,
      getAddress: getAddressFn,
      getContact: getContactFn,
      orderTotal,
      dispatch: dispatch as unknown as (action: unknown) => unknown,
      onCheckout,
      onCreateCardConnectPopup,
      onCreateErrorPopup,
      onEditCheckout,
    }),
    [
      cart,
      items_metadata,
      addresses,
      contacts,
      checkout,
      getAddressFn,
      getContactFn,
      orderTotal,
      dispatch,
      onCheckout,
      onCreateCardConnectPopup,
      onCreateErrorPopup,
      onEditCheckout,
    ],
  );
}
